import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { pageview as gaPageview } from "@/lib/gtag";
import { API } from "@/App";
import { isExcludedFromStats } from "@/lib/analytics";

// Persist an anonymous session id across pageloads so we can dedupe unique
// sessions on the admin analytics dashboard. NOT tied to identity.
function getSessionId() {
  try {
    let s = localStorage.getItem("tbc_session_id");
    if (!s) {
      s =
        (typeof crypto !== "undefined" && crypto.randomUUID?.()) ||
        `s_${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
      localStorage.setItem("tbc_session_id", s);
    }
    return s;
  } catch {
    return "s_anon";
  }
}

/**
 * PageviewTracker — fire-and-forget component mounted once at App root.
 * On every route change:
 *   1. Ping Google Analytics (if REACT_APP_GA4_MEASUREMENT_ID is set)
 *   2. POST /api/analytics/track { event_type: "page_view", metadata: {page} }
 *      so the in-app admin dashboard has a site-wide traffic view too.
 *
 * Renders nothing.
 */
export default function PageviewTracker() {
  const location = useLocation();

  useEffect(() => {
    const path = location.pathname + location.search;

    // 1. Google Analytics 4 — deep analytics (referrer, geo, device, funnels)
    gaPageview(path);

    // 2. Our own analytics_events collection — used by the admin dashboard.
    //    Silent failure — never break the page over a missed analytics call.
    (async () => {
      try {
        await axios.post(
          `${API}/analytics/track`,
          {
            event_type: "page_view",
            session_id: getSessionId(),
            metadata: { page: location.pathname },
          },
          {
            withCredentials: true, // send cookie so backend can detect admin
            timeout: 3000,
            headers: isExcludedFromStats() ? { "X-TBC-Exclude-From-Stats": "1" } : {},
          }
        );
      } catch {
        /* swallow */
      }
    })();
  }, [location.pathname, location.search]);

  return null;
}
